// Purpose: Detect Flutter InAppWebView and expose bridge helpers for native OAuth.
// Mistake: isInApp() is false before flutter_inappwebview attaches; OAuth must use isAppWebViewContext + waitForInAppBridge.
// Mistake: OAuth click on web waited up to ~5.5s for bridge probe; add quick probe path for login/signup entry.
export const isInApp = () => {
    // 아래 코드가 더 안전한 방법입니다.
    // return typeof (window as any).flutter_inappwebview !== "undefined";

    // 추후 위 코드로 변경 
    return (window as any).flutter_inappwebview || false;
}

export const waitForInAppBridge = async (
    timeoutMs: number = 1800,
    stepMs: number = 120
): Promise<boolean> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        if ((window as any)?.flutter_inappwebview?.callHandler) {
            return true;
        }
        await new Promise((resolve) => window.setTimeout(resolve, stepMs));
    }
    return !!(window as any)?.flutter_inappwebview?.callHandler;
};

/** True when running inside thpe-app WebView; waits for late bridge injection (matches dev CDN latency). */
export async function isAppWebViewContext(
    timeoutMs: number = 5500,
    stepMs: number = 100
): Promise<boolean> {
    if (typeof window === "undefined") {
        return false;
    }
    if ((window as any).flutter_inappwebview) {
        return true;
    }
    return waitForInAppBridge(timeoutMs, stepMs);
}

/** Fast probe for OAuth entry buttons: avoids long web delays while keeping tiny bridge-injection grace. */
export async function isAppWebViewContextQuick(
    timeoutMs: number = 250,
    stepMs: number = 50
): Promise<boolean> {
    if (typeof window === "undefined") {
        return false;
    }
    if ((window as any).flutter_inappwebview) {
        return true;
    }
    return waitForInAppBridge(timeoutMs, stepMs);
}

export const getOsType = async () => {
    const OSType = await (window as any)?.flutter_inappwebview?.callHandler("getOSType") || "web";
    return OSType;
}

export const getIsIos = async () => {
    const osType = await getOsType();
    return (osType === "iOS");
};

/** 코인 상품 API용: 실행 환경에 따른 salesChannelCode (web / ios / android) */
export const getSalesChannelForCoinProducts = async (): Promise<"salesChannel:web" | "salesChannel:ios" | "salesChannel:android"> => {
    if (!isInApp()) {
        return "salesChannel:web";
    }
    const isIos = await getIsIos();
    return isIos ? "salesChannel:ios" : "salesChannel:android";
};

/** 앱 내 스타디아 구매 시작 (네이티브 IAP 호출). Flutter 측에서 startStadiaPurchase 핸들러 구현 필요. */
export const startStadiaPurchase = async (params: {
    coinProductId: number;
    skuId: number | null;
    storeSkuId: string | null;
}): Promise<void> => {
    const bridge = (window as any).flutter_inappwebview;
    if (!bridge?.callHandler) {
        console.warn("[startStadiaPurchase] App bridge not available");
        return;
    }
    await bridge.callHandler("startStadiaPurchase", params);
};

export const getAppVersion = async () => {
    const appVersion = await (window as any)?.flutter_inappwebview?.callHandler("getAppVersion");
    return appVersion;
}

export const callAppStore = async (isIos: boolean) => {
    const iOSLink = "itms-apps://itunes.apple.com/app/6677023178";
    const androidLink = "https://play.google.com/store/apps/details?id=com.theethru.wannabes";
    await (window as any).flutter_inappwebview
        .callHandler(
            "executeExternalLink",
            isIos ? iOSLink : androidLink
        )(window as any)
        .flutter_inappwebview.callHandler("exitApp");
}

export const setNotiFilterByPath = async (path: string) => {
    if (path.includes("chat")) {
        await setNotiFilterByType("chat");
    } else {
        await setNotiFilterByType("");
    }
}

export const setNotiFilterByType = async (type: string) => {
    await (window as any)?.flutter_inappwebview?.callHandler("setNotiFilter", type);
}

export const getDeviceId = async () => {
    return await (window as any)?.flutter_inappwebview?.callHandler("getDeviceId");
}

export const getPushToken = async () => {
    return await (window as any)?.flutter_inappwebview?.callHandler("getPushToken");
}

export const setBadgeCount = (count: number) => {
    (window as any)?.flutter_inappwebview?.callHandler("setBadgeCount", `${count}`);
}

export const clearBadgeCount = () => {
    (window as any)?.flutter_inappwebview?.callHandler("clearBadgeCount");
}

export const checkHasPushPermission = async () => {
    return await (window as any)?.flutter_inappwebview?.callHandler("hasPushPermission");
}

export const openAppSetting = async () => {
    await (window as any)?.flutter_inappwebview?.callHandler("openAppSetting");
}

export const setData = async (key: string, value: string) => {
    await (window as any)?.flutter_inappwebview?.callHandler("setData", key, value);
}

export const getData = async (key: string) => {
    return await (window as any)?.flutter_inappwebview?.callHandler("getData", key);
}

export const removeData = async (key: string) => {
    await (window as any)?.flutter_inappwebview?.callHandler("removeData", key);
}

export const hideSplash = async () => {
    await (window as any)?.flutter_inappwebview?.callHandler("hideSplash", {});
}

export const compareVesion = (appVersion: string, serverVersion: string): boolean => {
    // Split version strings by '.'
    const appParts = appVersion.split('.');
    const serverParts = serverVersion.split('.');

    const maxLength = Math.max(appParts.length, serverParts.length);

    for (let i = 0; i < maxLength; i++) {
        const appPart = appParts[i] || "0";
        const serverPart = serverParts[i] || "0";
        if (appPart !== serverPart) {
            return true;
        }
    }
    return false; // 모든 부분이 같을 경우
}

// 현재 사용하지 않음
//  2026.02.24
export const getStatusBarHeight = async () => {
    return await (window as any)?.flutter_inappwebview?.callHandler("getStatusBarHeight");
}

// 현재 사용하지 않음
//  2026.02.24
export const getNavigationBarHeight = async () => {
    return await (window as any)?.flutter_inappwebview?.callHandler("getNavigationBarHeight");
}

// ─── Web → App 브리지 (IAP 규약) ─────────────────────────────────────────────
// productId: 스토어 상품 ID(문자열), requestId: 웹 추적용(선택)

export const purchaseConsumable = (params: { productId: string; requestId?: string }) => {
    (window as any)?.flutter_inappwebview?.callHandler("purchaseConsumable", {
        productId: params.productId,
        requestId: params.requestId ?? "",
    });
};

export const purchaseSubscription = (params: { productId: string; requestId?: string }) => {
    (window as any)?.flutter_inappwebview?.callHandler("purchaseSubscription", {
        productId: params.productId,
        requestId: params.requestId ?? "",
    });
};

export const restorePurchases = (params: { requestId?: string }) => {
    (window as any)?.flutter_inappwebview?.callHandler("restorePurchases", {
        requestId: params.requestId ?? "",
    });
};

/** 서버 검증 결과를 앱에 전달. requireSigned=true면 token 필수, false면 verified 사용. */
export const purchaseVerifyResult = (params: {
    verifyKey: string;
    verified?: boolean;
    token?: string;
    reason?: string;
}) => {
    (window as any)?.flutter_inappwebview?.callHandler("purchaseVerifyResult", params);
};

// hideSplash는 상단에 이미 정의됨: callHandler('hideSplash')

// ─── App → Web 이벤트 (앱이 window.onFlutterPurchaseEvent 호출) ─────────────

export const PURCHASE_EVENT_CODES = {
    purchase_started: "purchase_started",
    pending: "pending",
    verify_required: "verify_required",
    verified_purchase: "verified_purchase",
    iap_not_available: "iap_not_available",
    product_query_error: "product_query_error",
    verification_rejected: "verification_rejected",
    error: "error",
} as const;

export type PurchaseEventCode = (typeof PURCHASE_EVENT_CODES)[keyof typeof PURCHASE_EVENT_CODES];

/** verify_required 시 extra 필드 */
export interface VerifyRequiredExtra {
    verifyKey: string;
    requireSigned: boolean;
    purchase: unknown; // 서버 검증 API로 전달할 영수증/토큰 정보
}

export interface FlutterPurchaseEventPayload {
    ok: boolean;
    code: PurchaseEventCode;
    productId?: string;
    message?: string;
    extra?: VerifyRequiredExtra & Record<string, unknown>;
}

declare global {
    interface Window {
        onFlutterPurchaseEvent?: (event: FlutterPurchaseEventPayload) => void;
    }
}

/** 앱이 결제 이벤트를 보낼 때 호출할 콜백 등록. 앱 로드 시 한 번 등록 권장. */
export const registerFlutterPurchaseEvent = (handler: (event: FlutterPurchaseEventPayload) => void) => {
    window.onFlutterPurchaseEvent = handler;
};

export const unregisterFlutterPurchaseEvent = () => {
    window.onFlutterPurchaseEvent = undefined;
};